<script setup lang='ts'>
  import { computed } from 'vue'

  import { useImage } from '@/composables/useImage'
  import type { ImageAspectRatio } from '@/types/Image'
  import { TaskImageAlign, type StepTask } from '@/types/Task'

  import ImageWithCaption from '@/components/common/ImageWithCaption.vue'
  import RichTextRenderer from '@/components/common/RichTextRenderer.vue'
  import StepTaskLeftColumn from '@/components/task/StepTaskLeftColumn.vue'

  const props = defineProps<{
    step: number
    task: StepTask
    currentPage: number
    totalPages: number
    isFocusFooterVisible: boolean
  }>()

  const { src, altText, caption, copyright } = useImage({
    imageId: props.task?.image?.destinationContentId,
    svgId: props.task?.svg?.destinationContentId,
  })

  const isVisible = computed(() => props.currentPage === props.step)
  
  const imageAspectRatio = computed((): ImageAspectRatio => {
    switch (props.task?.imageAlign) {
    case TaskImageAlign.Stretch:
      return ''
    case TaskImageAlign.Portrait:
      return '2/3'
    case TaskImageAlign.Landscape:
    default:
      return '3/2'
    }
  })
  const isRightColumnRichText = computed(() => !!props.task?.rightColumnRichText)
</script>

<template>
  <main
    v-if="task"
    v-show="isVisible"
    :aria-hidden="!isVisible"
    class="step-task"
    data-track-context="stegoppgave"
  >
    <div class="container">
      <div class="left">
        <StepTaskLeftColumn
          :task="task"
          :current-page="currentPage"
          :total-pages="totalPages"
        />
      </div>
    </div>

    <div
      v-if="src || isRightColumnRichText"
      class="container"
    >
      <div
        class="right"
        :class="{ stretch: task.imageAlign === TaskImageAlign.Stretch, 'right-column-rich-text': isRightColumnRichText}"
      >
        <ImageWithCaption
          v-if="src && !isRightColumnRichText"
          :src="src"
          :alt-text="altText"
          :caption="caption"
          :copyright="copyright"
          :aspect-ratio="imageAspectRatio"
          :object-fit="'cover'"
          class="image"
        />
        <RichTextRenderer
          v-if="isRightColumnRichText"
          :rich-text="task.rightColumnRichText"
        />
      </div>
    </div>
    <div
      id="audio-embed-outlet"
      :class="['audio-embed-outlet', { 'audio-embed-outlet-no-footer': !isFocusFooterVisible }]"
    />
  </main>
  <slot v-if="isVisible" name="footer" />
</template>

<style lang='scss' scoped>
  .step-task {
    display: flex;
    overflow-y: auto;
    height: 100%;

    @include vgs-media-mobile-and-tablet {
      flex-direction: column;
    }

    .container {
      flex: 1;
      display: flex;
      flex-direction: column;
      position: relative;
    }

    .left {
      overflow-y: auto;
    }

    .left,
    .right {
      flex: 1;
      padding: 8vh 5vw 40px;

      @include horizontal-padding-mobile;
    }

    .right {
      background-color: var(--color-violet-10);
      height: 100%;
      padding-top: 10vh;
    }

    .right-column-rich-text {
      margin: 0;
      overflow: auto;
      height: 100%;
      background-color: var(--color-yellow-20);

      &:has(.geogebra-applet) {
        padding: 1rem;
        display: flex;
        justify-content: center;
        align-items: center;
        max-width: none;
      }

      :deep(.pronunciation-collection-wrapper) {
        padding-top: 0;

        .pronunciation-content {
          width: 100%;

          .pronunciation-collection {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }
        }


      }
    }

    .stretch {
      padding: 0;

      :deep(.image-with-caption-container) {
        height: 100%;
      }
    }
  }

  .image {
    max-height: 100%;
    margin-left: auto;
    margin-right: auto;
    width: initial;
  }

  .audio-embed-outlet {
    position: fixed;
    bottom: 66px;
    width: 100%;
    border-radius: 6px;
    z-index: 20;

    &-no-footer {
      bottom: 0;
    }
  }

  :deep(.media-controls-wrapper) {
    border-radius: 6px;
    margin: 8px;
  }

  @include video-overlay;
</style>